// SPDX-License-Identifier: AGPL-3.0-or-later

import GeoIP from '@app/features/app/state/GeoIP';
import Channels from '@app/features/channel/state/Channels';
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import Guilds from '@app/features/guild/state/Guilds';
import {
	getEffectiveChannelContentWarning,
	resolveEffectiveChannelNsfw,
} from '@app/features/messaging/utils/ContentWarningUtils';
import {getEffectiveNSFWGeoContext} from '@app/features/moderation/utils/NSFWGeoUtils';
import {makeSyncedField} from '@app/features/user/state/SyncedField';
import Users from '@app/features/user/state/Users';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {ContentWarningLevel} from '@app/vendor/constants/GuildConstants';
import {GuildNsfwAgreementsSchema} from '@app/vendor/schema/gen/fluxer/user/preferences/v1/preferences_pb';
import {makeAutoObservable} from 'mobx';
export enum NSFWGateReason {
	NONE = 0,
	GEO_RESTRICTED = 1,
	AGE_RESTRICTED = 2,
	CONSENT_REQUIRED = 3,
}
export type AgreementScope = 'channel' | 'category' | 'guild';
export interface NSFWGateContext {
	channelId?: string | null;
	guildId?: string | null;
}
export interface ResolvedGateContext {
	channelId: string | null;
	categoryId: string | null;
	guildId: string | null;
	effectiveNsfw: boolean;
	nsfwSource: 'channel' | 'parent' | 'guild' | 'none';
	effectiveWarningLevel: number;
	effectiveWarningText: string | null;
	warningSource: 'channel' | 'parent' | 'guild' | 'none';
	scope: AgreementScope;
	scopeId: string | null;
}
class GuildNSFWAgree {
	agreedChannelIds: Array<string> = [];
	agreedCategoryIds: Array<string> = [];
	agreedGuildIds: Array<string> = [];
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
	}
	private async initPersistence(): Promise<void> {
		await makeSyncedField(this, {
			field: 'guildNsfwAgreements',
			schema: GuildNsfwAgreementsSchema,
			persist: ['agreedChannelIds', 'agreedCategoryIds', 'agreedGuildIds'],
			toMessage: (s) => ({
				agreedChannelIds: [...s.agreedChannelIds],
				agreedCategoryIds: [...s.agreedCategoryIds],
				agreedGuildIds: [...s.agreedGuildIds],
			}),
			applyMessage: (s, m) => {
				s.agreedChannelIds = [...m.agreedChannelIds];
				s.agreedCategoryIds = [...m.agreedCategoryIds];
				s.agreedGuildIds = [...m.agreedGuildIds];
			},
		});
	}
	agreeToChannel(channelId: string): void {
		if (!this.agreedChannelIds.includes(channelId)) {
			this.agreedChannelIds.push(channelId);
		}
	}
	agreeToCategory(categoryId: string): void {
		if (!this.agreedCategoryIds.includes(categoryId)) {
			this.agreedCategoryIds.push(categoryId);
		}
	}
	agreeToGuild(guildId: string): void {
		if (!this.agreedGuildIds.includes(guildId)) {
			this.agreedGuildIds.push(guildId);
		}
	}
	reset(): void {
		this.agreedChannelIds = [];
		this.agreedCategoryIds = [];
		this.agreedGuildIds = [];
	}
	revokeChannel(channelId: string): void {
		this.agreedChannelIds = this.agreedChannelIds.filter((id) => id !== channelId);
	}
	revokeCategory(categoryId: string): void {
		this.agreedCategoryIds = this.agreedCategoryIds.filter((id) => id !== categoryId);
	}
	revokeGuild(guildId: string): void {
		this.agreedGuildIds = this.agreedGuildIds.filter((id) => id !== guildId);
	}
	hasAgreedToChannel(channelId: string): boolean {
		return this.agreedChannelIds.includes(channelId);
	}
	hasAgreedToCategory(categoryId: string): boolean {
		return this.agreedCategoryIds.includes(categoryId);
	}
	hasAgreedToGuild(guildId: string): boolean {
		return this.agreedGuildIds.includes(guildId);
	}
	private resolveContext(context: NSFWGateContext): ResolvedGateContext {
		const channelId = context.channelId ?? null;
		const guildIdFromArg = context.guildId ?? null;
		const channel = channelId ? Channels.getChannel(channelId) : null;
		const guildId = guildIdFromArg ?? channel?.guildId ?? null;
		const guild = guildId ? Guilds.getGuild(guildId) : null;
		let categoryId: string | null = null;
		if (channel) {
			if (channel.type === ChannelTypes.GUILD_CATEGORY) {
				categoryId = channel.id;
			} else if (channel.parentId) {
				const parent = Channels.getChannel(channel.parentId);
				if (parent && parent.type === ChannelTypes.GUILD_CATEGORY) {
					categoryId = parent.id;
				}
			}
		}
		let effectiveNsfw = false;
		let nsfwSource: ResolvedGateContext['nsfwSource'] = 'none';
		let effectiveWarningLevel: number = ContentWarningLevel.INHERIT;
		let effectiveWarningText: string | null = null;
		let warningSource: ResolvedGateContext['warningSource'] = 'none';
		if (channel) {
			const nsfwResult = resolveEffectiveChannelNsfw(channel, guild);
			const warning = getEffectiveChannelContentWarning(channel, guild);
			effectiveNsfw = nsfwResult.value;
			nsfwSource = nsfwResult.source;
			effectiveWarningLevel = warning.level;
			effectiveWarningText = warning.text;
			warningSource = warning.source;
		} else if (guild) {
			effectiveNsfw = guild.nsfw;
			nsfwSource = 'guild';
			effectiveWarningLevel = guild.contentWarningLevel ?? ContentWarningLevel.INHERIT;
			effectiveWarningText = guild.contentWarningText ?? null;
			warningSource = guild.contentWarningLevel !== ContentWarningLevel.INHERIT ? 'guild' : 'none';
		}
		const sourceForScope = effectiveNsfw
			? nsfwSource
			: effectiveWarningLevel === ContentWarningLevel.CONTENT_WARNING
				? warningSource
				: 'none';
		let scope: AgreementScope = 'channel';
		let scopeId: string | null = channelId;
		if (sourceForScope === 'guild') {
			scope = 'guild';
			scopeId = guildId;
		} else if (sourceForScope === 'parent') {
			scope = 'category';
			scopeId = categoryId;
		} else if (sourceForScope === 'channel') {
			scope = 'channel';
			if (channel && channel.type === ChannelTypes.GUILD_CATEGORY) {
				scope = 'category';
				scopeId = channel.id;
			} else {
				scopeId = channelId;
			}
		}
		return {
			channelId,
			categoryId,
			guildId,
			effectiveNsfw,
			nsfwSource,
			effectiveWarningLevel,
			effectiveWarningText,
			warningSource,
			scope,
			scopeId,
		};
	}
	getResolvedContext(context: NSFWGateContext): ResolvedGateContext {
		return this.resolveContext(context);
	}
	isGatedContent(context: NSFWGateContext): boolean {
		const {effectiveNsfw, effectiveWarningLevel} = this.resolveContext(context);
		return effectiveNsfw || effectiveWarningLevel === ContentWarningLevel.CONTENT_WARNING;
	}
	private hasEffectiveAgreement(resolved: ResolvedGateContext): boolean {
		if (resolved.guildId && this.hasAgreedToGuild(resolved.guildId)) return true;
		if (resolved.scope === 'category' && resolved.scopeId) {
			return this.hasAgreedToCategory(resolved.scopeId);
		}
		if (resolved.scope === 'channel') {
			if (resolved.categoryId && this.hasAgreedToCategory(resolved.categoryId)) return true;
			if (resolved.scopeId && this.hasAgreedToChannel(resolved.scopeId)) return true;
			return false;
		}
		return false;
	}
	getGateReason(context: NSFWGateContext): NSFWGateReason {
		const mockReason = DeveloperOptions.mockNSFWGateReason;
		if (mockReason !== 'none') {
			switch (mockReason) {
				case 'geo_restricted':
					return NSFWGateReason.GEO_RESTRICTED;
				case 'age_restricted':
					return NSFWGateReason.AGE_RESTRICTED;
				case 'consent_required':
					return NSFWGateReason.CONSENT_REQUIRED;
			}
		}
		const resolved = this.resolveContext(context);
		const hasWarning = resolved.effectiveWarningLevel === ContentWarningLevel.CONTENT_WARNING;
		if (!resolved.effectiveNsfw && !hasWarning) {
			return NSFWGateReason.NONE;
		}
		if (resolved.effectiveNsfw) {
			const currentUser = Users.getCurrentUser();
			const {countryCode, regionCode} = getEffectiveNSFWGeoContext();
			const ageRestrictedGeos = GeoIP.ageRestrictedGeos;
			if (countryCode) {
				const isAgeRestricted = ageRestrictedGeos.some((geo) => {
					if (geo.countryCode !== countryCode) return false;
					if (geo.regionCode === null) return true;
					return geo.regionCode === regionCode;
				});
				if (isAgeRestricted && !currentUser?.ageVerifiedAdult) {
					return NSFWGateReason.GEO_RESTRICTED;
				}
			}
			if (currentUser && !currentUser.nsfwAllowed) {
				return NSFWGateReason.AGE_RESTRICTED;
			}
		}
		if (this.hasEffectiveAgreement(resolved)) {
			return NSFWGateReason.NONE;
		}
		return NSFWGateReason.CONSENT_REQUIRED;
	}
	shouldShowGate(context: NSFWGateContext): boolean {
		return this.getGateReason(context) !== NSFWGateReason.NONE;
	}
	getGuildLevelGateReason(guildId: string): NSFWGateReason {
		return this.getGateReason({guildId, channelId: null});
	}
}
export default new GuildNSFWAgree();
